import { VEHICLE_ENUMS, getEnumOptions } from '../../constants/vehicleEnums';

import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Card } from 'react-bootstrap';
import { getBrands, getModelsByBrand } from '../../services/vehicleDataService';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import CarExpertizMap from '../CarExpertizMap';

// Helper function for text cleanup
const cleanTextInput = (value, removeSpaces = false) => {
    let cleaned = value.trim().toUpperCase();
    if (removeSpaces) {
        cleaned = cleaned.replace(/\s+/g, '');
    }
    return cleaned;
};

const VehicleBasicInfoForm = ({ data, handleChange, validationErrors = {}, hideBrandModel = false, getFieldHighlightClass }) => {
    // ✅ BRANDS/MODELS STATE'LERİ
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [brandsLoading, setBrandsLoading] = useState(false);
    const [modelsLoading, setModelsLoading] = useState(false);

    // ✅ BRANDS FETCH useEffect
    useEffect(() => {
        const loadBrands = async () => {
            try {
                setBrandsLoading(true);
                const response = await getBrands();
                if (response.success) {
                    setBrands(response.data || []);
                }
            } catch (error) {
                console.error('Brands load error:', error);
            } finally {
                setBrandsLoading(false);
            }
        };

        loadBrands();
    }, []);

    // ✅ MODELS FETCH useEffect - data.brand değişince tetiklenir
    useEffect(() => {
        const loadModels = async () => {
            if (!data.brand) {
                setModels([]);
                return;
            }

            try {
                setModelsLoading(true);
                const response = await getModelsByBrand(data.brand);
                if (response.success) {
                    setModels(response.data || []);
                }
            } catch (error) {
                console.error('Models load error:', error);
                setModels([]);
            } finally {
                setModelsLoading(false);
            }
        };

        loadModels();
    }, [data.brand]);


    const getTechnicalDetailValue = (fieldName) => {
        return data.technicalDetails?.[fieldName] || '';
    };

    // ✅ BRAND/MODEL CHANGE HANDLERS
    const handleBrandChange = useCallback((e) => {
        const brand = e.target.value;
        handleChange({
            target: { name: 'brand', value: brand }
        });
        handleChange({
            target: { name: 'model', value: '' }
        });

        const newTitle = brand ? `${brand} ${data.model || ''}`.trim() : data.title;
        if (newTitle !== data.title) {
            handleChange({
                target: { name: 'title', value: newTitle }
            });
        }
    }, [handleChange, data.model, data.title]);

    const handleModelChange = useCallback((e) => {
        const modelName = e.target.value;
        const selectedModel = models.find(m => m.name === modelName);

        handleChange({
            target: { name: 'model', value: modelName }
        });

        if (selectedModel && selectedModel.type) {
            handleChange({
                target: { name: 'bodyType', value: selectedModel.type }
            });
        }

        const newTitle = `${data.brand || ''} ${modelName}`.trim();
        if (newTitle !== data.title) {
            handleChange({
                target: { name: 'title', value: newTitle }
            });
        }
    }, [handleChange, models, data.brand, data.title]);

    const colors = [
        'Beyaz', 'Siyah', 'Gri', 'Gümüş', 'Kırmızı', 'Mavi', 'Yeşil',
        'Sarı', 'Turuncu', 'Mor', 'Kahverengi', 'Bej', 'Metalik'
    ];

    // ✅ Validasyon durumunu kontrol et
    const getFieldValidationClass = (fieldName) => {
        if (validationErrors[fieldName]) return 'is-invalid';
        if (data[fieldName] && !validationErrors[fieldName]) return 'is-valid';
        return '';
    };


    // ✅ Combined field classes
    const getFieldClasses = (fieldName) => {
        const validationClass = getFieldValidationClass(fieldName);
        const highlightClass = getFieldHighlightClass ? getFieldHighlightClass(fieldName) : '';
        return `form-control ${validationClass} ${highlightClass}`.trim();
    };

    // Plaka format validation
    const validatePlateFormat = (plate) => {
        if (!plate) return false;
        const plateRegex = /^[0-9]{2}\s?[A-Z]{1,3}\s?[0-9]{1,4}$/;
        return plateRegex.test(plate.toUpperCase());
    };

    // Şase numarası validation
    const validateChassisNumber = (chassis) => {
        return chassis && chassis.length === 17;
    };

    const getCurrentYear = () => new Date().getFullYear();

    // ✅ CRITICAL FIX: Parent handleChange function için dispatch helper
    const handleFormDataChange = useCallback((fieldName, fieldValue) => {
        console.log(`📝 Form data change: ${fieldName} = ${fieldValue}`);

        handleChange({
            target: {
                name: fieldName,
                value: fieldValue
            }
        });
    }, [handleChange]);

    useEffect(() => {
        if (data.technicalDetails) {
            console.log('🔄 Technical details updated in form:', data.technicalDetails);
        }
    }, [data.technicalDetails]);

    const validateTechnicalDetails = () => {
        const errors = [];
        const td = data.technicalDetails || {};

        if (td.engineDisplacement && (td.engineDisplacement < 0 || td.engineDisplacement > 10000)) {
            errors.push('Motor hacmi 0-10000 cc arasında olmalıdır');
        }

        if (td.horsepower && (td.horsepower < 0 || td.horsepower > 2000)) {
            errors.push('Motor gücü 0-2000 kW arasında olmalıdır');
        }

        return errors;
    };


    return (
        <Card className="mb-4">
            <Card.Header>
                <h5 className="mb-0">🚗 Araç Temel Bilgileri</h5>
            </Card.Header>
            <Card.Body>
                {/* ✅ MARKA/MODEL SEÇİMİ - EN ÜSTTE */}
                <div className="mb-4">
                    <h6 className="mb-3">🏷️ Marka & Model</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Marka <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={data.brand || ''}
                                    onChange={handleBrandChange}
                                    disabled={brandsLoading}
                                    required
                                    className={getFieldValidationClass('brand')}
                                >
                                    <option value="">
                                        {brandsLoading ? 'Yükleniyor...' : 'Marka seçiniz'}
                                    </option>
                                    {brands.map((brand) => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    {brands.length > 0 ? `${brands.length} marka mevcut` : ''}
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Model <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={data.model || ''}
                                    onChange={handleModelChange}
                                    disabled={!data.brand || modelsLoading}
                                    required
                                    className={getFieldValidationClass('model')}
                                >
                                    <option value="">
                                        {!data.brand ? 'Önce marka seçiniz' :
                                            modelsLoading ? 'Yükleniyor...' :
                                                'Model seçiniz'}
                                    </option>
                                    {models.map((model) => (
                                        <option key={model.name} value={model.name}>
                                            {model.name} {model.type && `(${model.type})`}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    {data.brand && models.length > 0 ? `${models.length} model mevcut` : ''}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </div>

                {/* ✅ Yasal Zorunlu Bilgiler - Üstte */}
                <div className="mb-4">
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Plaka Numarası <span className="text-danger">*</span>
                                    {data.plateNumber && !validatePlateFormat(data.plateNumber) && (
                                        <small className="text-danger ms-2">⚠️ Format hatası</small>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="plateNumber"
                                    value={data.plateNumber || ''}
                                    onChange={(e) => {
                                        const cleanValue = cleanTextInput(e.target.value, true);
                                        handleChange({
                                            target: {
                                                name: 'plateNumber',
                                                value: cleanValue
                                            }
                                        });
                                    }}
                                    placeholder="34ABC123"
                                    maxLength="8"
                                    className={getFieldClasses('plateNumber')}
                                />
                                <Form.Text className="text-muted">Örnek: 34 ABC 123</Form.Text>
                                {data.plateNumber && validatePlateFormat(data.plateNumber) && (
                                    <div className="text-success small">✅ Geçerli format</div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Şase Numarası <span className="text-danger">*</span>
                                    {data.chassisNumber && !validateChassisNumber(data.chassisNumber) && (
                                        <small className="text-danger ms-2">⚠️ 17 karakter</small>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="chassisNumber"
                                    value={data.chassisNumber || ''}
                                    onChange={(e) => {
                                        const cleanValue = cleanTextInput(e.target.value, false);
                                        handleChange({
                                            target: {
                                                name: 'chassisNumber',
                                                value: cleanValue
                                            }
                                        });
                                    }}
                                    placeholder="VF1..."
                                    maxLength="17"
                                    className={getFieldClasses('chassisNumber')}
                                />
                                <Form.Text className="text-muted">({data.chassisNumber?.length || 0}/17)</Form.Text>
                                {data.chassisNumber && validateChassisNumber(data.chassisNumber) && (
                                    <div className="text-success small">✅ Geçerli şase</div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    İlan Başlığı <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    value={data.title}
                                    onChange={handleChange}
                                    placeholder="2020 Toyota Corolla 1.4 D-4D"
                                    maxLength={200}
                                    className={getFieldValidationClass('title')}
                                    required
                                />
                                <Form.Text className="text-muted">{data.title?.length || 0}/200</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </div>

                {/* ✅ Araç Teknik Bilgileri - ENHANCED */}
                <div className="border-top pt-3 mb-4">
                    <h6 className="mb-3">⚙️ Teknik Bilgiler</h6>

                    {/* Temel Teknik Bilgiler */}
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Model Yılı <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="number"
                                    name="year"
                                    value={data.year}
                                    onChange={handleChange}
                                    placeholder="2020"
                                    min="1950"
                                    max={getCurrentYear() + 1}
                                    className={getFieldValidationClass('year')}
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Yakıt Tipi</Form.Label>
                                <Form.Select
                                    name="fuelType"
                                    value={data.fuelType || 'Benzin'}
                                    onChange={handleChange}
                                >
                                    <option value="Benzin">Benzin</option>
                                    <option value="Dizel">Dizel</option>
                                    <option value="LPG">LPG</option>
                                    <option value="Elektrik">Elektrik</option>
                                    <option value="Hibrit">Hibrit</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Vites Tipi</Form.Label>
                                <Form.Select
                                    name="transmission"
                                    value={data.transmission || 'Manuel'}
                                    onChange={handleChange}
                                >
                                    {getEnumOptions('transmission').map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Kilometre</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        name="mileage"
                                        value={data.mileage}
                                        onChange={handleChange}
                                        placeholder="150000"
                                        min="0"
                                        className={getFieldValidationClass('mileage')}
                                    />
                                    <span className="input-group-text">km</span>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* ✅ YENİ: Gelişmiş Teknik Bilgiler */}
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Silindir Hacmi</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        name="engineDisplacement"
                                        value={getTechnicalDetailValue('engineDisplacement')}
                                        onChange={(e) => {
                                            const updatedTechnicalDetails = {
                                                ...data.technicalDetails,
                                                [e.target.name]: e.target.value
                                            };
                                            handleChange({
                                                target: {
                                                    name: 'technicalDetails',
                                                    value: updatedTechnicalDetails
                                                }
                                            });
                                        }}
                                        placeholder="1600"
                                        min="0"
                                        step="1"
                                    />
                                    <span className="input-group-text">cc</span>
                                </div>
                                <Form.Text className="text-muted">Örnek: 1600</Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Motor Gücü</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        name="horsepower"
                                        value={getTechnicalDetailValue('horsepower')}
                                        onChange={(e) => {
                                            const updatedTechnicalDetails = {
                                                ...data.technicalDetails,
                                                [e.target.name]: e.target.value
                                            };
                                            handleChange({
                                                target: {
                                                    name: 'technicalDetails',
                                                    value: updatedTechnicalDetails
                                                }
                                            });
                                        }}
                                        placeholder="90"
                                        min="0"
                                        step="1"
                                    />
                                    <span className="input-group-text">kW</span>
                                </div>
                                <Form.Text className="text-muted">Kilowatt gücü</Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Garanti</Form.Label>
                                <Form.Select
                                    name="warrantyStatus"
                                    value={getTechnicalDetailValue('warrantyStatus')}
                                    onChange={(e) => {
                                        const updatedTechnicalDetails = {
                                            ...data.technicalDetails,
                                            [e.target.name]: e.target.value
                                        };
                                        handleChange({
                                            target: {
                                                name: 'technicalDetails',
                                                value: updatedTechnicalDetails
                                            }
                                        });
                                    }}
                                >
                                    <option value="">Özel Açıklama</option>
                                    <option value="Var">Garanti Var</option>
                                    <option value="Yok">Garanti Yok</option>
                                </Form.Select>

                                {/* Conditional Text Input */}
                                {getTechnicalDetailValue('warrantyStatus') === '' && (
                                    <Form.Control
                                        type="text"
                                        name="warrantyCustom"
                                        value={getTechnicalDetailValue('warrantyCustom')}
                                        onChange={(e) => {
                                            const updatedTechnicalDetails = {
                                                ...data.technicalDetails,
                                                [e.target.name]: e.target.value
                                            };
                                            handleChange({
                                                target: {
                                                    name: 'technicalDetails',
                                                    value: updatedTechnicalDetails
                                                }
                                            });
                                        }}
                                        placeholder="Garanti durumunu açıklayın (örn: 2 yıl fabrika garantisi)"
                                        className="mt-2"
                                        maxLength={100}
                                    />
                                )}
                            </Form.Group>
                            </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Muayene Tarihi</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="inspectionDate"
                                    value={getTechnicalDetailValue('inspectionDate')}
                                    onChange={(e) => {
                                        const updatedTechnicalDetails = {
                                            ...data.technicalDetails,
                                            [e.target.name]: e.target.value
                                        };
                                        handleChange({
                                            target: {
                                                name: 'technicalDetails',
                                                value: updatedTechnicalDetails
                                            }
                                        });
                                    }}
                                />
                                <Form.Text className="text-muted">Son muayene</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                   
                    {/* Diğer Bilgiler */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Renk</Form.Label>
                                <Form.Select
                                    name="color"
                                    value={data.color}
                                    onChange={handleChange}
                                    className={getFieldValidationClass('color')}
                                >
                                    <option value="">Seçin</option>
                                    {colors.map(color => (
                                        <option key={color} value={color}>{color}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ek Özellikler</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="description"
                                    value={data.description}
                                    onChange={handleChange}
                                    placeholder="Aracın ek özellikleri..."
                                    maxLength={2000}
                                />
                                <Form.Text className="text-muted">{data.description?.length || 0}/2000</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </div>

                {/* ✅ YENİ: Etiket Bilgileri */}
                <div className="border-top pt-3 mb-4">
                    <h6 className="mb-3">🏷️ Etiket Bilgileri</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sahibinden URL</Form.Label>
                                <Form.Control
                                    type="url"
                                    name="sahibindenUrl"
                                    value={getTechnicalDetailValue('sahibindenUrl')}
                                    onChange={(e) => {
                                        const updatedTechnicalDetails = {
                                            ...data.technicalDetails,
                                            [e.target.name]: e.target.value
                                        };
                                        handleChange({
                                            target: {
                                                name: 'technicalDetails',
                                                value: updatedTechnicalDetails
                                            }
                                        });
                                    }}
                                    placeholder="https://www.sahibinden.com/ilan/..."
                                />
                                <Form.Text className="text-muted">
                                    Orijinal ilan linki (etiket oluşturmak için)
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        
                    </Row>
                </div>

                {/* ✅ Tramer Bilgileri - Compact */}
                <div className="border-top pt-3">
                    <h6 className="mb-3">🚨 Tramer Bilgileri</h6>
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Tramer Durumu</Form.Label>
                                <Form.Select
                                    name="hasTramer"
                                    value={data.hasTramer ? 'true' : 'false'}
                                    onChange={(e) => {
                                        const event = {
                                            target: {
                                                name: 'hasTramer',
                                                value: e.target.value === 'true'
                                            }
                                        };
                                        handleChange(event);
                                    }}
                                >
                                    <option value="false">Tramer Yok</option>
                                    <option value="true">Tramer Var</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        {data.hasTramer && (
                            <>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Tramer Miktarı (₺)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="tramerAmount"
                                            value={data.tramerAmount || ''}
                                            onChange={handleChange}
                                            placeholder="0"
                                            min="0"
                                            step="any"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Tramer Açıklaması</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="tramerDescription"
                                            value={data.tramerDescription || ''}
                                            onChange={handleChange}
                                            placeholder="Tramer detayları"
                                        />
                                    </Form.Group>
                                </Col>
                            </>
                        )}
                    </Row>

                    {data.hasTramer && (
                        <div className="small text-warning">
                            ⚠️ <strong>Uyarı:</strong> Satış sırasında müşteriye bildirilmelidir.
                        </div>
                    )}
                </div>

                {/* ✅ Finansman Bilgileri */}
                <div className="border-top pt-3">
                    <h6 className="mb-3">💰 Finansman</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Kasko Kodu</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="kaskoKodu"
                                    value={data.kaskoKodu || ''}
                                    onChange={handleChange}
                                    placeholder="1541294"
                                    className="text-muted"
                                    min="0"
                                    step="1"
                                />
                                <Form.Text className="text-muted">
                                    Kasko poliçe referans kodu
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Kasko Bedeli</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        name="kaskoBedeli"
                                        value={data.kaskoBedeli || ''}
                                        onChange={handleChange}
                                        placeholder="1500000"
                                        min="0"
                                        step="1000"
                                    />
                                    <span className="input-group-text">₺</span>
                                </div>
                                <Form.Text className="text-muted">
                                    {data.kaskoBedeli ?
                                        `${new Intl.NumberFormat('tr-TR').format(data.kaskoBedeli)} TL` :
                                        'Kasko değeri (TL)'}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </div>

                {/* ✅ Expertiz Bilgileri */}
                <div className="border-top pt-3">
                    <h6 className="mb-3">🔍 Expertiz Durumu</h6>

                    <CarExpertizMap
                        initialExpertizData={data.expertizData || {}}
                        onChange={(expertizData) => {
                            handleChange({
                                target: {
                                    name: 'expertizData',
                                    value: expertizData
                                }
                            });
                        }}
                        disabled={false}
                        showLegend={true}
                    />

                    {/* Expertiz Notları */}
                    <div className="mt-3">
                        <Form.Group className="mb-3">
                            <Form.Label>Expertiz Notları</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="expertizNotes"
                                value={data.expertizNotes || ''}
                                onChange={handleChange}
                                placeholder="Expertiz ile ilgili ek notlar..."
                                maxLength={1000}
                            />
                            <Form.Text className="text-muted">
                                {(data.expertizNotes || '').length}/1000
                            </Form.Text>
                        </Form.Group>
                    </div>
                </div>

            </Card.Body>
        </Card>
    );
};

export default VehicleBasicInfoForm;